import { ReactElement } from 'react';
import { useTranslation } from 'next-i18next';

import Button from '@Hotels-at-Home/hah-fe-core/dist/components/Button';
import TitleFlip from '../../helpers/TitleFlip';
import { sanitizeHtml } from '@Hotels-at-Home/hah-fe-core/dist/utils';

const BourbonSection = (): ReactElement => {
  const { t } = useTranslation('', { keyPrefix: '$pages.$home.$bourbonSection' });
  const { t: tButton } = useTranslation('', { keyPrefix: '$common.$buttons' });
  const { t: tCommon } = useTranslation('', { keyPrefix: '$common' });
  const { t: tLink } = useTranslation('', { keyPrefix: '$local.$homePageLinks.$bourbon' });

  return (
    <section className="base-section bourbon-section">
      <div className="desktop-viewport">
        <div className="bourbon">
          <h2 className="section-title title-center" data-gsap="fade-up" data-gsap-delay="0.6">
            <TitleFlip normalText={t('collection')} alternateFlip>
              <span className="accent-bar">
                {t('bourbon')}
                <em data-gsap="grow" data-gsap-delay="0.8" data-gsap-duration="0.8" />
              </span>
            </TitleFlip>
          </h2>
          <div className="bourbon-products">
            <div id="hpBourbonGlasses" className="bourbon-product-wrap">
              <a
                href={tLink('bourbonGlasses')}
                className="hover-btn-animation"
                id="HP_bourbon_glasses"
              >
                <picture>
                  <source srcSet="/images/homePage/fill.gif" media="(max-width: 800px)" />
                  <source srcSet="/images/homePage/bourbonSection/hp-bourbon-glasses.webp" />
                  <img
                    data-gsap="fade"
                    data-gsap-delay="0.4"
                    src="/images/homePage/bourbonSection/hp-bourbon-glasses.webp"
                    alt={tCommon('productName', { productName: t('bourbonGlasses') })}
                    loading="lazy"
                    width="550"
                    height="430"
                    decoding="async"
                    fetchPriority="low"
                  />
                </picture>
                <h3 className="section-subtitle">
                  <span data-gsap="fade-left" data-gsap-delay="0.6">
                    {t('bourbonGlasses')}
                  </span>
                </h3>
                <div className="hover-content">
                  <Button noTabbing ariaLabel={`${tButton('shopNow')}: ${t('bourbonGlasses')}`}>
                    {tButton('shopNow')}
                  </Button>
                </div>
              </a>
            </div>

            <div id="hpBourbonMixer" className="bourbon-product-wrap">
              <a
                href={tLink('mixingSet')}
                className="hover-btn-animation"
                id="HP_bourbon_mixing-set"
              >
                <picture>
                  <source srcSet="/images/homePage/fill.gif" media="(max-width: 800px)" />
                  <source srcSet="/images/homePage/bourbonSection/hp-mixer-set.webp" />
                  <img
                    data-gsap="fade"
                    data-gsap-delay="0.4"
                    src="/images/homePage/bourbonSection/hp-mixer-set.webp"
                    alt={tCommon('productName', { productName: t('cocktailMixingSet') })}
                    loading="lazy"
                    width="550"
                    height="430"
                    decoding="async"
                    fetchPriority="low"
                  />
                </picture>
                <h3 className="section-subtitle">
                  <span data-gsap="fade-left" data-gsap-delay="0.6">
                    {t('mixingSet')}
                  </span>
                </h3>
                <div className="hover-content">
                  <Button noTabbing ariaLabel={`${tButton('shopNow')}: ${t('mixingSet')}`}>
                    {tButton('shopNow')}
                  </Button>
                </div>
              </a>
            </div>

            <div id="hpBourbonMold" className="bourbon-product-wrap ">
              <a
                href={tLink('iceCubeMold')}
                className="hover-btn-animation"
                id="HP_bourbon_ice-cube-mold"
              >
                <picture>
                  <source srcSet="/images/homePage/fill.gif" media="(max-width: 800px)" />
                  <source srcSet="/images/homePage/bourbonSection/hp-ice-cube-mold.webp" />
                  <img
                    data-gsap="fade"
                    data-gsap-delay="0.4"
                    src="/images/homePage/bourbonSection/hp-ice-cube-mold.webp"
                    alt={tCommon('productName', { productName: t('clearIceCubeMold') })}
                    loading="lazy"
                    width="550"
                    height="430"
                    decoding="async"
                    fetchPriority="low"
                  />
                </picture>
                <h3 className="section-subtitle">
                  <span data-gsap="fade-left" data-gsap-delay="0.6">
                    {t('iceCubeMold')}
                  </span>
                </h3>
                <div className="hover-content">
                  <Button noTabbing ariaLabel={`${tButton('shopNow')}: ${t('iceCubeMold')}`}>
                    {tButton('shopNow')}
                  </Button>
                </div>
              </a>
            </div>

            <div id="hpYaraiMixingBeaker" className="bourbon-product-wrap">
              <a
                href={tLink('yaraiMixingBeaker')}
                className="hover-btn-animation"
                id="HP_bourbon_yarai-mixing-beaker"
              >
                <picture>
                  <source srcSet="/images/homePage/fill.gif" media="(max-width: 800px)" />
                  <source srcSet="/images/homePage/bourbonSection/hp-yarai-mixer-beaker.webp" />
                  <img
                    data-gsap="fade"
                    data-gsap-delay="0.4"
                    src="/images/homePage/bourbonSection/hp-yarai-mixer-beaker.webp"
                    alt={tCommon('productName', { productName: t('yaraiMixingBeaker') })}
                    loading="lazy"
                    width="550"
                    height="430"
                    decoding="async"
                    fetchPriority="low"
                  />
                </picture>
                <h3 className="section-subtitle">
                  <span data-gsap="fade-left" data-gsap-delay="0.6">
                    {t('yaraiMixingBeaker')}
                  </span>
                </h3>
                <div className="hover-content">
                  <Button noTabbing ariaLabel={`${tButton('shopNow')}: ${t('yaraiMixingBeaker')}`}>
                    {tButton('shopNow')}
                  </Button>
                </div>
              </a>
            </div>

            <div id="hpBourbonKit" className="bourbon-product-wrap">
              <a
                href={tLink('cocktailKit')}
                className="hover-btn-animation"
                id="HP_bourbon_cocktail-kit"
              >
                <picture>
                  <source srcSet="/images/homePage/fill.gif" media="(max-width: 800px)" />
                  <source srcSet="/images/homePage/bourbonSection/hp-cocktail-kit.webp" />
                  <img
                    data-gsap="fade"
                    data-gsap-delay="0.4"
                    src="/images/homePage/bourbonSection/hp-cocktail-kit.webp"
                    alt={tCommon('productName', { productName: t('oldFashioned') })}
                    loading="lazy"
                    width="1123"
                    height="574"
                    decoding="async"
                    fetchPriority="low"
                  />
                </picture>
                <h3 className="section-subtitle">
                  <span data-gsap="fade-left" data-gsap-delay="0.6">
                    {t('oldFashioned')}
                  </span>
                </h3>
                <div className="hover-content">
                  <Button noTabbing ariaLabel={`${tButton('shopNow')}: ${t('cocktailMixingSet')}`}>
                    {tButton('shopNow')}
                  </Button>
                </div>
              </a>
            </div>

            <div className="bourbon-video-wrap">
              <div className="bourbon-stack">
                <h3 className="section-subtitle">{t('howToMake')}</h3>
                <div className="video-wrap" data-gsap="fade" data-gsap-delay="0.4">
                  <div id="bourbonVideo">
                    <iframe
                      frameBorder="0"
                      allowFullScreen
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      title={t('youtubeVideoPlayer')}
                      src="https://www.youtube.com/embed/1LJmpK0Wzx4?rel=0&amp;controls=1&amp;enablejsapi=1&amp;origin=https%3A%2F%2Fwww.shopmarriott.com&amp;widgetid=1"
                      loading="lazy"
                    />
                  </div>
                </div>
                <p className="section-txt">{t('samAwabdeh')}</p>
                {/* Image is hidden between 801 and 970 to prevent overflow for some languages */}
                <picture>
                  <source srcSet="/images/homePage/fill.gif" media="(max-width: 970px)" />
                  <source srcSet="/images/homePage/bourbonSection/hp-old-fashioned-drink.webp" />
                  <img
                    data-gsap="fade-right"
                    data-gsap-delay="0.4"
                    src="/images/homePage/bourbonSection/hp-old-fashioned-drink.webp"
                    alt={t('howToMake')}
                    loading="lazy"
                    width="1280"
                    height="557"
                    decoding="async"
                    fetchPriority="low"
                  />
                </picture>
                <p data-gsap="fade-up" data-gsap-delay="0.45">
                  <Button
                    href={tLink('bourbonCollection')}
                    id="btn_HP_bourbon_shopnow"
                    ariaLabel={`${tButton('shopNow')}: ${t('bourbonGlasses')}`}
                    variant="plain"
                  >
                    {tButton('shopNow')}
                  </Button>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="hp-mobile-viewport">
        <h2 className="mob-title">
          <TitleFlip normalText={t('collection')} alternateFlip>
            <span className="accent-bar">
              {t('bourbon')}
              <em data-gsap="grow" data-gsap-delay="0.06" data-gsap-duration="0.8" />
            </span>
          </TitleFlip>
        </h2>
        <a className="mob-main-section" href={tLink('bourbonCollection')} id="CTA-bourbon-mob">
          <picture>
            <source srcSet="/images/homePage/fill.gif" media="(min-width: 801px)" />
            <source srcSet="/images/homePage/bourbonSection/hp-bourbon-mob.webp" />
            <img
              src="/images/homePage/bourbonSection/hp-bourbon-mob.webp"
              alt={t('bourbon')}
              className="img"
              loading="lazy"
              width="786"
              height="419"
              decoding="async"
              fetchPriority="low"
            />
          </picture>
        </a>
        <div className="shop-list">
          <a
            className="shop-list-item"
            href={tLink('yaraiMixingBeaker')}
            id="CTA-hpYaraiMixingBeaker-mob"
          >
            <picture>
              <source srcSet="/images/homePage/fill.gif" media="(min-width: 801px)" />
              <source srcSet="/images/homePage/bourbonSection/MAREU-722-YR-shop-marriott-yarai-mixing-beaker_hp_mob.webp" />
              <img
                src="/images/homePage/bourbonSection/MAREU-722-YR-shop-marriott-yarai-mixing-beaker_hp_mob.webp"
                alt={tCommon('productName', { productName: t('yaraiMixingBeaker') })}
                className="img"
                loading="lazy"
                width="1123"
                height="732"
                decoding="async"
                fetchPriority="low"
              />
            </picture>
            <h3 className="mob-section mob-section-half-width txt">{t('yaraiMixingBeaker')}</h3>
          </a>
          <a className="shop-list-item" href={tLink('bourbonGlasses')} id="CTA-glasses-mob">
            <picture>
              <source srcSet="/images/homePage/fill.gif" media="(min-width: 801px)" />
              <source srcSet="/images/homePage/bourbonSection/marriott-Bourbon-Glasses-MAR-721-BB-1-9_lrg.webp" />
              <img
                src="/images/homePage/bourbonSection/marriott-Bourbon-Glasses-MAR-721-BB-1-9_lrg.webp"
                alt={tCommon('productName', { productName: t('bourbonGlasses') })}
                className="img"
                loading="lazy"
                width="1123"
                height="732"
                decoding="async"
                fetchPriority="low"
              />
            </picture>
            <h3
              className="mob-section mob-section-half-width txt"
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(t('bourbonGlasses')) }}
            />
          </a>
        </div>
      </div>
    </section>
  );
};

export default BourbonSection;
